import * as React from "react";
import {Fragment, useCallback, useEffect, useState} from "react";
import {CubeOrder, Order, Shop} from "../types";
import {DeliveryFlow} from "./DeliveryFlow";
import {ShopAccessHome, ShopHomeTab} from "./home";
import {PickupFlow} from "./PickupFlow";
import {Occupancy} from "@variocube/app-api";
import {OrderDetail} from "./OrderDetail";
import {logger} from "../logger";
import {
    classNames,
    Dialog,
    DialogAlignment,
    layout,
    PushButton,
    theme,
    useCountdown,
    useIdle,
    usePreventAutoDeactivate
} from "@variocube/cube-ui";
import {useLocalization} from "../../i18n";

const log = logger.sub("shop");

interface ShopAccessProps {
    shop: Shop;
    onDone: () => void;
    allowOpenNonOccupied: boolean;
    orderDelivery?: Order;
}

export function ShopAccessView({shop, onDone, allowOpenNonOccupied, orderDelivery}: ShopAccessProps) {
    const [delivery, setDelivery] = useState<Order | undefined>(orderDelivery);
    const [pickup, setPickup] = useState<Occupancy>();
    const [shopHomeTab, setShopHomeTab] = useState<ShopHomeTab>(ShopHomeTab.Orders);
    const [detail, setDetail] = useState<CubeOrder>();

    const handleDeliveryDone = useCallback(() => {
        setDelivery(undefined);
        setDetail(undefined);
        if (orderDelivery && orderDelivery.storageCode){
            onDone();
        }
    }, []);
    const handlePickupDone = useCallback(() => {
        setPickup(undefined);
        setDetail(undefined);
    }, []);
    const handleDetailClose = useCallback(() => setDetail(undefined), []);

    useEffect(() => log.verbose("Tab activated", shopHomeTab), [shopHomeTab]);

    usePreventAutoDeactivate();

    const idle = useIdle(60);
    const left = useCountdown(30, idle);

    useEffect(() => {
        if (!delivery && !pickup && left < 0) {
            onDone();
        }
    }, [delivery, pickup, left, onDone]);

    const {t} = useLocalization();

    if (delivery) {
        return (
            <DeliveryFlow
                order={delivery}
                shop={shop}
                onDone={handleDeliveryDone}
            />
        );
    }
    if (pickup) {
        return (
            <PickupFlow
                occupancy={pickup}
                onDone={handlePickupDone}
            />
        );
    }

    return (
        <Fragment>
            {detail && (
                <OrderDetail
                    detail={detail}
                    onDelivery={setDelivery}
                    onPickup={setPickup}
                    onClose={handleDetailClose}
                />
            )}
            {!detail && (
                <ShopAccessHome
                    shop={shop}
                    active={shopHomeTab}
                    onTabActivated={setShopHomeTab}
                    onOpen={setDetail}
                    onDone={onDone}
                    allowOpenNonOccupied={allowOpenNonOccupied}
                />
            )}
            <Dialog open={idle} align={DialogAlignment.Center}>
                <div className={classNames(layout.vertical, layout.alignCenter)}>
                    <div className={classNames(theme.moreContrast, layout.paddingMedium)}>
                        {t("CleanerAutoLogoutMessage", {remainingSeconds: left})}
                    </div>
                    <div className={classNames(layout.paddingMedium)}>
                        <PushButton>
                            {t("Close")}
                        </PushButton>
                    </div>
                </div>
            </Dialog>
        </Fragment>
    )

}
